import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../hooks/useAuth";
import { useAuthStore } from "../../lib/auth-store";
import {
  type RosaPlayer,
  useAggiungiAllaLista25,
  useCapitanoStagione,
  useRiapriLista25,
  useRimuoviDaLista25,
  useRosa,
  useSetPoolCapitani,
  useSwapRosa,
  useUfficializzaLista25,
} from "../../hooks/useFase2";
import {
  useAllenatoriSecondi,
  useInvitaAllenatoreSecondo,
  useMieSquadreGestite,
  useRimuoviAllenatoreSecondo,
  useSquadre,
  useUpdateLogoSquadra,
} from "../../hooks/useFase7";
import { useStagioneCorrente } from "../../hooks/useStagione";
import { faseGiornate, type GiornataFase } from "../dashboard/faseGiornate";
import { api } from "../../lib/api";
import { MobileShell } from "../shell/MobileShell";
import { PlayerRosterRow } from "../common/PlayerRosterRow";
import { FasciaBraccio } from "../common/FasciaBraccio";
import { FULL_BY_GROUP } from "../common/ruoliColors";
import { TeamBadge } from "../common/TeamBadge";
import { TrofeiSquadra } from "../common/TrofeiSquadra";
import { preparaLogo } from "../common/imageCompress";
import { LogoSfondoModal } from "./LogoSfondoModal";
import { CediSquadraModal } from "./CediSquadraModal";
import { Maglia } from "../common/Maglia";
import {
  MagliaEditor,
  MAGLIA_DEFAULT,
  magliaDaSquadra,
  magliaPayload,
  type MagliaState,
} from "../common/MagliaEditor";
import { useUpdateMagliaSquadra } from "../../hooks/useFase7";
import { sigleRuoli } from "../common/nomenclatura";
import { tr } from "../../i18n";

const PENALE_RINOMINA_M = 25;

/** Andrea 2026-05-22: usa palette centralizzata ruoliColors. La label
 * mostrata viene comunque sovrascritta dai ruoli mantra (se presenti)
 * — vedi `PlayerCard` sotto. */
const RUOLO_TAG: Record<string, { label: string; bg: string }> = {
  P: { label: "Por", bg: FULL_BY_GROUP.Por },
  D: { label: "Dif", bg: FULL_BY_GROUP.Def },
  C: { label: "Cen", bg: FULL_BY_GROUP.Mid },
  A: { label: "Att", bg: FULL_BY_GROUP.Att },
};

/** Ordine canonico per il sort dei giocatori in rosa: P → D → C → A.
 * Andrea (2026-05-18): no piu' ordinamento alfabetico, sempre per ruolo. */
const RUOLO_ORDER: Record<string, number> = { P: 0, D: 1, C: 2, A: 3 };

type FilterChip = "tutti" | "P" | "D" | "C" | "A";

/** Compara due giocatori per ruolo (P→D→C→A), poi per nome. */
function compareByRuolo(a: RosaPlayer, b: RosaPlayer): number {
  const ra = RUOLO_ORDER[classicLetter(a.ruolo_classic)] ?? 99;
  const rb = RUOLO_ORDER[classicLetter(b.ruolo_classic)] ?? 99;
  if (ra !== rb) return ra - rb;
  return a.nome.localeCompare(b.nome, "it");
}

interface RinominaResponse {
  squadra_id: string;
  nome: string;
  numero_cambi_nome: number;
  penale_applicata_milioni: number | null;
}

function classicLetter(ruoloClassic: string | null): string {
  if (!ruoloClassic) return "?";
  const c = ruoloClassic.charAt(0).toUpperCase();
  if (["P", "D", "C", "A"].includes(c)) return c;
  return "?";
}

interface PlayerCardProps {
  p: RosaPlayer;
  onConvoca?: (p: RosaPlayer) => void;
  onEscludi?: (p: RosaPlayer) => void;
  busy?: boolean;
}

function PlayerCard({ p, onConvoca, onEscludi, busy }: PlayerCardProps) {
  // Andrea 2026-09-08: layout unificato con Rose (PlayerRosterRow):
  // riga ad altezza fissa, ruoli mantra impilati a sinistra, poi
  // avatar, nome (+U23) e sotto valore TM · club. Qui in più, a
  // destra, i controlli convoca/escludi e lo stato.
  return (
    <PlayerRosterRow
      p={p}
      right={
        <>
          {p.tipo_iscrizione === "ufficiale" && onEscludi && (
            <button
              type="button"
              onClick={() => onEscludi(p)}
              disabled={busy}
              className="px-1.5 py-0.5 text-[9px] sm:px-2 sm:py-1 sm:text-[10px] whitespace-nowrap rounded bg-danger/10 text-danger border border-danger/30 hover:bg-danger/20 disabled:opacity-50"
              title={tr("Rimuovi dalla lista 25 (downgrade a provvisoria)")}
            >
              {tr("✕ Escludi")}
            </button>
          )}
          {p.tipo_iscrizione === "provvisoria" && onConvoca && (
            <button
              type="button"
              onClick={() => onConvoca(p)}
              disabled={busy}
              className="px-1.5 py-0.5 text-[9px] sm:px-2 sm:py-1 sm:text-[10px] whitespace-nowrap rounded bg-success/10 text-success border border-success/30 hover:bg-success/20 disabled:opacity-50"
              title={tr("Aggiungi alla lista 25 ufficiale")}
            >
              {tr("＋ Convoca")}
            </button>
          )}
          <div
            className={`pill text-[9px] sm:text-[10px] px-1.5 sm:px-2 whitespace-nowrap ${
              p.tipo_iscrizione === "ufficiale"
                ? "bg-success/20 text-success"
                : "bg-warning/20 text-warning"
            }`}
          >
            {/* Andrea 2026-05-22: label "Convocato"/"Non convocato" piu'
                parlante dell'astratto "ufficiale"/"provvisoria". */}
            {p.tipo_iscrizione === "ufficiale" ? tr("Convocato") : tr("Non convocato")}
          </div>
        </>
      }
    />
  );
}

export function RosaPage() {
  const { user } = useAuth();
  const qc = useQueryClient();

  const { data: squadre } = useSquadre();
  const { data: stagioneAttiva } = useStagioneCorrente();
  const stagioneId = stagioneAttiva?.id ?? "";
  // Andrea 2026-05-21: include anche le squadre dove sei allenatore in seconda.
  const { data: idsGestite } = useMieSquadreGestite();
  const mieSquadre =
    squadre?.filter(
      (s) => s.presidente_user_id === user?.id || (idsGestite ?? []).includes(s.id),
    ) ?? [];

  // Finestra mercato attiva per gating dei bottoni admin (Andrea
  // 2026-05-20): "Ufficializza 25" e "Riapri lista 25" devono essere
  // disponibili SOLO se c'è una finestra mercato attiva.
  //
  // Andrea 2026-05-21: i due bottoni Ufficializza/Riapri sono stati
  // accorpati in un toggle unico "🔓 Lista APERTA ↔ 🔒 Lista CHIUSA".
  // Lo stato lista deriva da `rose_finalizzate_at` sulla finestra attiva
  // (o sull'ultima finestra della stagione se nessuna è attiva).
  const isAuth = !!useAuthStore((s) => s.accessToken);
  const { data: finestreMercato } = useQuery({
    queryKey: ["stagione-finestre", stagioneId],
    queryFn: async () =>
      (
        await api.get<
          Array<{
            id: string;
            attiva: boolean;
            rose_finalizzate_at: string | null;
          }>
        >(`/admin/stagioni/${stagioneId}/finestre`)
      ).data,
    enabled: isAuth && !!stagioneId,
  });
  const finestraAttiva = finestreMercato?.find((f) => f.attiva) ?? null;
  const mercatoAperto = !!finestraAttiva;
  // Stato lista 25: chiusa se la finestra di riferimento ha
  // `rose_finalizzate_at` valorizzato. A mercato chiuso (nessuna finestra
  // attiva) consideriamo sempre la lista chiusa — backend forza
  // rose_finalizzate_at=now() alla chiusura della finestra.
  const listaChiusa = !mercatoAperto || !!finestraAttiva?.rose_finalizzate_at;

  // Auto-seleziona la prima squadra del presidente loggato (se esiste)
  const [squadraId, setSquadraId] = useState<string>(mieSquadre[0]?.id ?? "");
  // Quando il fetch arriva e l'utente non ha ancora selezionato, popoliamo
  if (mieSquadre.length > 0 && !squadraId) {
    setSquadraId(mieSquadre[0].id);
  }

  const [filter, setFilter] = useState<FilterChip>("tutti");
  const [showCapModal, setShowCapModal] = useState(false);

  const { data: rosa, isLoading } = useRosa(stagioneId || null, squadraId || null);
  const { data: capStagione } = useCapitanoStagione(stagioneId || null, squadraId || null);
  const setCapMut = useSetPoolCapitani();
  const convocaMut = useAggiungiAllaLista25();
  const escludiMut = useRimuoviDaLista25();
  const ufficializzaMut = useUfficializzaLista25();
  const riapriMut = useRiapriLista25();
  const swapMut = useSwapRosa();
  // Andrea 2026-10-22: niente swap a giornata in corso (turno partito e non
  // ancora calcolato): il backend lo rifiuta comunque, qui si spegne il
  // bottone e si spiega perche'. Stessa lettura delle giornate della home.
  const { data: giornateStagione } = useQuery({
    queryKey: ["stagione-giornate", stagioneId || null],
    queryFn: async () =>
      (await api.get<GiornataFase[]>(`/admin/stagioni/${stagioneId}/giornate`)).data,
    enabled: !!stagioneId,
  });
  const giornataInCorso = faseGiornate(giornateStagione, Date.now()).inCorso;
  // Andrea 2026-10-16: esito e errore dello swap in tema app (niente alert)
  const [swapErrore, setSwapErrore] = useState<string | null>(null);
  const [swapEsito, setSwapEsito] = useState<string | null>(null);
  const rosterBusy = convocaMut.isPending || escludiMut.isPending;
  const [showSwapModal, setShowSwapModal] = useState(false);
  const [showSecondiModal, setShowSecondiModal] = useState(false);
  const [showCediModal, setShowCediModal] = useState(false);

  // Permessi admin (presidente lega / admin / mod ruoli aggiuntivi).
  const isAdmin =
    user?.ruolo_lega === "presidente_lega" ||
    user?.ruolo_lega === "admin" ||
    !!user?.ruoli_aggiuntivi?.some((r) => r === "presidente_lega" || r === "admin");

  async function onConvoca(p: RosaPlayer) {
    if (!stagioneId || !squadraId) return;
    try {
      await convocaMut.mutateAsync({
        stagioneId,
        squadraId,
        giocatoreId: p.giocatore_id,
      });
    } catch (e) {
      const err = e as {
        response?: { data?: { detail?: string }; status?: number };
        message?: string;
      };
      const detail = err.response?.data?.detail ?? err.message ?? "errore";
      alert(
        err.response?.status === 409
          ? tr("Mercato chiuso: {{v1}}\n\nPer modificare la rosa ora serve uno SWAP (in arrivo).", {
              v1: detail,
            })
          : tr("Errore: {{v1}}", { v1: detail }),
      );
    }
  }

  async function onEscludi(p: RosaPlayer) {
    if (!stagioneId || !squadraId) return;
    if (!confirm(tr("Escludere {{v1}} dalla lista 25?", { v1: p.nome }))) return;
    try {
      await escludiMut.mutateAsync({
        iscrizioneId: p.iscrizione_id,
        stagioneId,
        squadraId,
      });
    } catch (e) {
      const err = e as {
        response?: { data?: { detail?: string }; status?: number };
        message?: string;
      };
      const detail = err.response?.data?.detail ?? err.message ?? "errore";
      alert(
        err.response?.status === 409
          ? tr("Mercato chiuso: {{v1}}", { v1: detail })
          : tr("Errore: {{v1}}", { v1: detail }),
      );
    }
  }

  // Filtra per ruolo + ordina sempre per ruolo (P→D→C→A, poi nome).
  const filteredItems = useMemo(() => {
    if (!rosa) return [];
    const base =
      filter === "tutti"
        ? rosa.items
        : rosa.items.filter((p) => classicLetter(p.ruolo_classic) === filter);
    return [...base].sort(compareByRuolo);
  }, [rosa, filter]);

  // Sezione "Lista 25 — Convocati" (tipo='ufficiale') vs "Fuori lista 25"
  // (tipo='provvisoria'). Entrambe ordinate per ruolo. Andrea (2026-05-18):
  // tenere divise visivamente, mai mescolare.
  const convocati = useMemo(
    () => filteredItems.filter((p) => p.tipo_iscrizione === "ufficiale"),
    [filteredItems],
  );
  const fuoriLista = useMemo(
    () => filteredItems.filter((p) => p.tipo_iscrizione === "provvisoria"),
    [filteredItems],
  );

  // Stats: usa i contatori della view (sempre presenti anche se items=[])
  const totale = rosa?.totale ?? 0;
  const ufficiali = rosa?.ufficiali ?? 0;
  // Andrea 2026-09-24: totali della rosa — pagato, TM all'acquisto, TM oggi.
  const valoriRosa = useMemo(() => {
    const items = rosa?.items ?? [];
    const somma = (f: (p: RosaPlayer) => string | null | undefined) =>
      items.reduce((t, p) => t + (f(p) != null ? Number(f(p)) : 0), 0);
    const conEntrambi = items.filter(
      (p) => p.valore_tm_milioni != null && p.valore_tm_iniziale_milioni != null,
    );
    return {
      pagato: somma((p) => p.prezzo_acquisto_milioni),
      tmIniziale: somma((p) => p.valore_tm_iniziale_milioni),
      tmOggi: somma((p) => p.valore_tm_milioni),
      variazione: conEntrambi.reduce(
        (t, p) => t + Number(p.valore_tm_milioni) - Number(p.valore_tm_iniziale_milioni),
        0,
      ),
    };
  }, [rosa]);
  const provvisorie = rosa?.provvisorie ?? 0;

  // === Rinomina mutation ===
  const rinominaMut = useMutation({
    mutationFn: async (args: { id: string; nuovoNome: string }) => {
      const res = await api.patch<RinominaResponse>(`/squadre/${args.id}/nome`, {
        nuovo_nome: args.nuovoNome,
      });
      return res.data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["squadre"] });
      if (data.penale_applicata_milioni) {
        alert(
          tr('Squadra rinominata in "{{v1}}".\\nPenale di {{v2}}M addebitata.', {
            v1: data.nome,
            v2: data.penale_applicata_milioni,
          }),
        );
      } else {
        alert(tr('Squadra rinominata in "{{v1}}". Primo cambio gratis.', { v1: data.nome }));
      }
    },
    onError: (err) => {
      const detail = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      alert(tr("Errore rinomina: {{v1}}", { v1: detail ?? String(err) }));
    },
  });

  const onRinomina = (squadra: { id: string; nome: string; numero_cambi_nome: number }) => {
    const isFree = squadra.numero_cambi_nome === 0;
    const intro = isFree
      ? tr('Stai rinominando "{{v1}}" (1° cambio gratuito).', { v1: squadra.nome })
      : tr('Stai rinominando "{{v1}}".\\n⚠ Penale {{v2}}M.', {
          v1: squadra.nome,
          v2: PENALE_RINOMINA_M,
        });
    const nuovo = prompt(tr("{{v1}}\n\nNuovo nome:", { v1: intro }), squadra.nome);
    if (!nuovo?.trim() || nuovo.trim() === squadra.nome) return;
    if (!isFree) {
      const ok = confirm(
        tr('Confermi cambio con penale {{v1}}M?\\nDa: "{{v2}}"\\nA: "{{v3}}"', {
          v1: PENALE_RINOMINA_M,
          v2: squadra.nome,
          v3: nuovo.trim(),
        }),
      );
      if (!ok) return;
    }
    rinominaMut.mutate({ id: squadra.id, nuovoNome: nuovo.trim() });
  };

  // === Logo squadra (Andrea 2026-05-20) ===
  // Upload base64 data-URL; rimozione = null. Validato lato server.
  const updateLogoMut = useUpdateLogoSquadra();
  // Andrea 2026-09-17: al caricamento lo sfondo a tinta unita si toglie da
  // soli; prima di salvare si vede il prima/dopo e si sceglie.
  const [logoDaScegliere, setLogoDaScegliere] = useState<{
    squadraId: string;
    originale: string;
    senzaSfondo: string;
  } | null>(null);
  const salvaLogo = (sqId: string, dataUrl: string) => {
    updateLogoMut.mutate(
      { squadraId: sqId, logoDataUrl: dataUrl },
      {
        onSuccess: () => setLogoDaScegliere(null),
        onError: (err) => {
          const detail = (err as { response?: { data?: { detail?: string } } }).response?.data
            ?.detail;
          alert(tr("Errore upload logo: {{v1}}", { v1: detail ?? String(err) }));
        },
      },
    );
  };
  const onCambiaLogo = (sqId: string) => {
    const input = document.createElement("input");
    input.type = "file";
    // image/* accetta anche foto del telefono (galleria/fotocamera) e SVG.
    input.accept = "image/*";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      if (!file.type.startsWith("image/")) {
        alert("File non valido: serve un'immagine.");
        return;
      }
      // Ridimensiona + comprime automaticamente sotto il limite server,
      // così anche foto/loghi grandi diventano caricabili (Andrea 2026-06-11).
      let logo: Awaited<ReturnType<typeof preparaLogo>>;
      try {
        logo = await preparaLogo(file);
      } catch {
        alert("Impossibile elaborare questa immagine. " + "Prova con un PNG/JPG/WebP standard.");
        return;
      }
      if (logo.senzaSfondo) {
        setLogoDaScegliere({
          squadraId: sqId,
          originale: logo.originale,
          senzaSfondo: logo.senzaSfondo,
        });
      } else {
        // Niente sfondo da togliere (gia' trasparente, foto): si carica com'e'.
        salvaLogo(sqId, logo.originale);
      }
    };
    input.click();
  };
  const onRimuoviLogo = (sqId: string) => {
    if (!confirm("Rimuovere il logo personalizzato? Torneranno le iniziali.")) return;
    updateLogoMut.mutate({ squadraId: sqId, logoDataUrl: null });
  };

  const squadraCorrente = mieSquadre.find((s) => s.id === squadraId);

  // Andrea 2026-09-24: Excel coi valori della rosa (prezzo pagato, TM
  // all'acquisto, TM di oggi), della squadra o di tutta la lega.
  const [scaricoValori, setScaricoValori] = useState<"squadra" | "lega" | null>(null);
  const scaricaValori = async (cosa: "squadra" | "lega") => {
    if (!stagioneId) return;
    setScaricoValori(cosa);
    try {
      const url =
        cosa === "lega"
          ? `/rose/lega/stagione/${stagioneId}/valori-tm.xlsx`
          : `/rose/${stagioneId}/${squadraId}/valori-tm.xlsx`;
      const res = await api.get(url, { responseType: "blob" });
      const blob = new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const nome =
        cosa === "lega"
          ? "valori_tm_lega.xlsx"
          : `valori_tm_${(squadraCorrente?.nome ?? "squadra").replace(/[^\w]+/g, "_")}.xlsx`;
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = nome;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    } catch (e) {
      const err = e as { response?: { data?: { detail?: string } }; message?: string };
      alert(`Errore export: ${err.response?.data?.detail ?? err.message ?? "errore"}`);
    } finally {
      setScaricoValori(null);
    }
  };

  // === Maglia squadra (Andrea 2026-06-11) ===
  const updateMagliaMut = useUpdateMagliaSquadra();
  const [magliaEditorOpen, setMagliaEditorOpen] = useState(false);
  const [magliaDraft, setMagliaDraft] = useState<MagliaState>(MAGLIA_DEFAULT);
  const onApriMaglia = () => {
    if (!squadraCorrente) return;
    setMagliaDraft(magliaDaSquadra(squadraCorrente));
    setMagliaEditorOpen(true);
  };
  const onSalvaMaglia = () => {
    if (!squadraCorrente) return;
    updateMagliaMut.mutate(
      {
        squadraId: squadraCorrente.id,
        maglia: magliaPayload(magliaDraft),
      },
      {
        onSuccess: () => setMagliaEditorOpen(false),
        onError: (err) => {
          const detail = (err as { response?: { data?: { detail?: string } } }).response?.data
            ?.detail;
          alert(`Errore salvataggio maglia: ${detail ?? String(err)}`);
        },
      },
    );
  };

  return (
    <MobileShell logoSuffix="ROSA">
      {/* Selettore squadra (solo se hai più di una) */}
      {mieSquadre.length > 1 && (
        <select
          value={squadraId}
          onChange={(e) => setSquadraId(e.target.value)}
          className="w-full px-3 py-2 bg-bg-card border border-primary/20 rounded-card text-fg"
        >
          {mieSquadre.map((s) => (
            <option key={s.id} value={s.id}>
              {s.nome}
            </option>
          ))}
        </select>
      )}

      {squadraCorrente && (
        // Andrea 2026-05-23: layout responsive — mobile (sotto md):
        // verticale (info squadra sopra, bottoni sotto in wrap).
        // Desktop: orizzontale come prima.
        <div className="bg-bg-card border border-primary/20 rounded-card p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <TeamBadge
              nome={squadraCorrente.nome}
              logoDataUrl={squadraCorrente.logo_data_url}
              size={48}
              variant="home"
              className="!mx-0"
            />
            <button
              type="button"
              onClick={onApriMaglia}
              title={tr("Personalizza la maglia")}
              className="shrink-0 hover:opacity-80 transition"
            >
              <Maglia
                pattern={squadraCorrente.maglia_pattern}
                coloreCorpo={squadraCorrente.maglia_colore_corpo}
                coloreDettaglio={squadraCorrente.maglia_colore_dettaglio}
                coloreManiche={squadraCorrente.maglia_colore_maniche}
                coloreAccento={squadraCorrente.maglia_colore_accento}
                coloreColletto={squadraCorrente.maglia_colore_colletto}
                colorePolsini={squadraCorrente.maglia_colore_polsini}
                coloreSponsor={squadraCorrente.maglia_colore_sponsor}
                sponsor={squadraCorrente.maglia_sponsor}
                patchScudetto={squadraCorrente.maglia_patch_scudetto}
                patchChampions={squadraCorrente.maglia_patch_champions}
                logoDataUrl={squadraCorrente.logo_data_url}
                size={56}
              />
            </button>
            <div className="min-w-0 flex-1">
              <div className="font-display text-xl text-accent truncate flex items-center gap-2">
                <span className="truncate">{squadraCorrente.nome}</span>
                <TrofeiSquadra squadraId={squadraCorrente.id} size="sm" />
              </div>
              {/* Andrea 2026-09-09: via il livello privacy da qui, non dice
                  niente di utile al presidente (si gestisce da /privacy). */}
              <div className="text-[10px] text-fg-muted font-mono mt-0.5">
                cambi nome: {squadraCorrente.numero_cambi_nome}
              </div>
              {/* Andrea 2026-05-20 — gestione logo personalizzato */}
              <div className="flex gap-2 mt-1.5 flex-wrap">
                <button
                  type="button"
                  onClick={() => onCambiaLogo(squadraCorrente.id)}
                  disabled={updateLogoMut.isPending}
                  className="text-[10px] uppercase tracking-wider text-accent hover:underline disabled:opacity-50"
                  title={tr(
                    tr(
                      "Carica un'immagine (PNG/JPG/WebP/SVG o foto): viene ridimensionata e, se ha uno sfondo a tinta unita, ritagliata in automatico",
                    ),
                  )}
                >
                  {squadraCorrente.logo_data_url ? tr("🖼 Cambia logo") : tr("🖼 Carica logo")}
                </button>
                {squadraCorrente.logo_data_url && (
                  <button
                    type="button"
                    onClick={() => onRimuoviLogo(squadraCorrente.id)}
                    disabled={updateLogoMut.isPending}
                    className="text-[10px] uppercase tracking-wider text-danger hover:underline disabled:opacity-50"
                  >
                    {tr("✕ Rimuovi")}
                  </button>
                )}
                <button
                  type="button"
                  onClick={onApriMaglia}
                  className="text-[10px] uppercase tracking-wider text-accent hover:underline"
                  title={tr("Personalizza la maglia della squadra")}
                >
                  {tr("👕 Maglia")}
                </button>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Link
              to="/rinnovi"
              className="btn-ghost text-xs border border-info/40 text-info whitespace-nowrap"
            >
              {tr("🔄 Rinnovi")}
            </Link>
            {/* Andrea 2026-05-21: gestione allenatori in seconda
                — solo il presidente diretto può invitare/rimuovere */}
            {squadraCorrente.presidente_user_id === user?.id && (
              <button
                type="button"
                onClick={() => setShowSecondiModal(true)}
                className="btn-ghost text-xs border border-accent/40 text-accent whitespace-nowrap"
                title={tr("Invita un socio come allenatore in seconda per questa squadra")}
              >
                {tr("👥 Allenatori")}
              </button>
            )}
            {/* Andrea 2026-10-08: cessione della squadra a un'altra persona */}
            {squadraCorrente.presidente_user_id === user?.id && (
              <button
                type="button"
                onClick={() => setShowCediModal(true)}
                className="btn-ghost text-xs border border-warning/40 text-warning hover:bg-warning/10 whitespace-nowrap"
                title={tr("Passa la squadra a un nuovo presidente con un invito via email")}
              >
                {tr("💼 Cedi squadra")}
              </button>
            )}
            <button
              type="button"
              onClick={() => onRinomina(squadraCorrente)}
              disabled={rinominaMut.isPending}
              // Andrea 2026-09-24: viola chiaro/rosa, per distinguerlo dagli
              // altri bottoni; se e' gratis o a pagamento lo dice il testo.
              className="btn-ghost text-xs btn-rinomina whitespace-nowrap"
            >
              {tr("✏️ Rinomina")}
              <span className="ml-1 text-[10px]">
                {squadraCorrente.numero_cambi_nome === 0
                  ? tr("(gratis)")
                  : `(−${PENALE_RINOMINA_M}M)`}
              </span>
            </button>
          </div>
        </div>
      )}

      {/* Azioni rosa.
          Andrea 2026-05-21: i due bottoni "Ufficializza" e "Riapri" sono
          stati accorpati in un toggle unico che mostra lo stato corrente
          della lista 25. A mercato chiuso il toggle e' disabled (lo stato
          e' sempre "chiuso" — il backend forza rose_finalizzate_at alla
          chiusura della finestra). */}
      {squadraCorrente && rosa && rosa.items.length > 0 && (
        <div className="flex gap-2 flex-wrap items-center">
          {isAdmin && stagioneId && (
            <button
              type="button"
              disabled={!mercatoAperto || ufficializzaMut.isPending || riapriMut.isPending}
              onClick={async () => {
                // Toggle: se chiusa → riapri; se aperta → ufficializza
                if (listaChiusa) {
                  if (
                    !confirm(
                      tr("Riaprire la lista 25?\n\n") +
                        tr("• Convoca/Escludi torneranno disponibili\n") +
                        tr("• Lo swap rosa non sarà più richiesto\n\n") +
                        tr("Conferma?"),
                    )
                  )
                    return;
                  try {
                    const r = await riapriMut.mutateAsync({ stagioneId });
                    alert(tr("✓ {{v1}}", { v1: r.messaggio }));
                  } catch (e) {
                    const err = e as {
                      response?: { data?: { detail?: string } };
                      message?: string;
                    };
                    alert(
                      tr("Errore: {{v1}}", {
                        v1: err.response?.data?.detail ?? err.message ?? "errore",
                      }),
                    );
                  }
                } else {
                  if (
                    !confirm(
                      tr("Chiudere la lista 25?\n\n") +
                        tr("Dopo questa azione:\n") +
                        tr("• Niente piu' convoca/escludi\n") +
                        tr("• Le rose sono congelate\n") +
                        tr("• Per modifiche solo 1 SWAP per squadra\n\n") +
                        tr("Conferma?"),
                    )
                  )
                    return;
                  try {
                    const r = await ufficializzaMut.mutateAsync({ stagioneId });
                    alert(tr("✓ {{v1}}", { v1: r.messaggio }));
                  } catch (e) {
                    const err = e as {
                      response?: { data?: { detail?: string } };
                      message?: string;
                    };
                    alert(
                      tr("Errore: {{v1}}", {
                        v1: err.response?.data?.detail ?? err.message ?? "errore",
                      }),
                    );
                  }
                }
              }}
              className={`btn-ghost text-xs border disabled:opacity-50 ${
                listaChiusa
                  ? "border-info/40 text-info hover:bg-info/10"
                  : "border-warning/40 text-warning hover:bg-warning/10"
              }`}
              title={
                !mercatoAperto
                  ? tr("A mercato chiuso lo stato è sempre 'lista chiusa'")
                  : listaChiusa
                    ? tr("Riapri lista 25 → torna ai convoca/escludi")
                    : tr("Chiudi e ufficializza la lista 25 (swap-only)")
              }
            >
              {listaChiusa
                ? tr("🔒 Lista CHIUSA · clicca per riaprire")
                : tr("🔓 Lista APERTA · clicca per chiudere")}
            </button>
          )}
          {!isAdmin && (
            <span
              className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded ${
                listaChiusa
                  ? "bg-danger/15 text-danger border border-danger/30"
                  : "bg-success/15 text-success border border-success/30"
              }`}
              title={
                listaChiusa
                  ? tr("La lista 25 è chiusa: niente più convoca/escludi")
                  : tr("Lista 25 modificabile (convoca/escludi attivi)")
              }
            >
              {listaChiusa ? tr("🔒 Lista CHIUSA") : tr("🔓 Lista APERTA")}
            </span>
          )}
          {/* Andrea 2026-05-21: Swap rosa visibile SOLO a mercato chiuso
              (nessuna finestra attiva). A mercato aperto, anche se la
              lista 25 e' chiusa (caso anomalo), si usano convoca/escludi.
              Backend: 1 swap per finestra mercato chiusa, contatore si
              azzera automaticamente alla chiusura di una nuova finestra. */}
          {!mercatoAperto && (
            <button
              type="button"
              onClick={() => setShowSwapModal(true)}
              disabled={!!giornataInCorso}
              className="btn-ghost text-xs border border-info/40 text-info hover:bg-info/10 disabled:opacity-50 disabled:cursor-not-allowed"
              title={
                giornataInCorso
                  ? tr(
                      "Giornata in corso: lo swap torna disponibile dopo il calcolo della giornata.",
                    )
                  : tr("Swap rosa: 1 per finestra chiusa, si azzera quando ne chiudi un'altra")
              }
            >
              {giornataInCorso ? tr("⏸ Swap rosa") : tr("↔ Swap rosa")}
            </button>
          )}
        </div>
      )}

      {/* Capitano + Vicecapitano stagionali (perenni). Click sulla card
          apre il modal di scelta. Andrea (2026-05-18): scelta unica per
          la stagione, non piu' per ogni formazione. */}
      {rosa && rosa.items.length > 0 && (
        <div className="bg-bg-card border border-primary/20 rounded-card p-3">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="text-xs uppercase tracking-wider font-bold text-fg-muted flex items-center gap-1.5 flex-wrap">
              {/* Andrea 2026-09-08: la fascia da capitano al posto della
                  stella — stessa grafica del campo. */}
              <span className="w-7 shrink-0 inline-block align-middle">
                <FasciaBraccio />
              </span>
              Papabili capitani{" "}
              <span className="font-normal normal-case text-[10px]">
                {tr("(5 · cap/vice si scelgono qui tra questi in Schiera Formazione)")}
              </span>
              {capStagione?.pool_congelato && (
                <span className="ml-1 text-[10px] text-warning">{tr("🔒 congelati")}</span>
              )}
            </div>
            <button
              type="button"
              onClick={() => setShowCapModal(true)}
              disabled={capStagione?.pool_congelato}
              className="text-[10px] uppercase tracking-wider text-accent hover:underline disabled:opacity-40 disabled:no-underline"
            >
              {(capStagione?.pool_giocatori_ids?.length ?? 0) > 0
                ? tr("Modifica")
                : tr("Scegli i 5")}
            </button>
          </div>
          {(capStagione?.pool_giocatori_ids?.length ?? 0) > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {capStagione!.pool_giocatori_ids.map((gid) => (
                <span key={gid} className="pill text-[11px] bg-accent/15 text-accent">
                  {rosa.items.find((p) => p.giocatore_id === gid)?.nome ?? gid.slice(0, 6)}
                </span>
              ))}
            </div>
          ) : (
            <div className="text-xs text-fg-muted">
              Nessun papabile scelto. Clicca "Scegli i 5" per definire i 5 giocatori tra cui
              sceglierai capitano e vice ad ogni giornata. Si congelano alla chiusura del mercato.
            </div>
          )}
        </div>
      )}

      {/* Stats pills. Andrea 2026-05-20: rosa totale limitata a 40
          (25 ufficiali + 15 provvisori). Mostro X/40 e Y/25 per chiarezza. */}
      <div className="grid grid-cols-3 gap-2">
        <div
          className={`bg-bg-card border rounded-card p-3 text-center ${
            totale >= 40
              ? "border-danger/60"
              : totale >= 36
                ? "border-warning/60"
                : "border-primary/20"
          }`}
          title={
            totale >= 40
              ? tr("Rosa al limite: rimuovi un giocatore prima di prenderne uno nuovo")
              : tr("{{v1}} slot liberi", { v1: 40 - totale })
          }
        >
          {/* Andrea 2026-05-22: colore totale → info (azzurro neutro)
              invece di rosso che era allarmistico. Soglie warning/danger
              rimangono per i casi limite. */}
          <div
            className={`font-display text-2xl leading-none ${
              totale >= 40 ? "text-danger" : totale >= 36 ? "text-warning" : "text-info"
            }`}
          >
            {totale}
            <span className="text-sm text-fg-muted">/40</span>
          </div>
          <div className="text-[10px] text-fg-muted uppercase tracking-wider mt-1">
            {tr("Totale")}
          </div>
        </div>
        <div
          className={`bg-bg-card border rounded-card p-3 text-center ${
            ufficiali >= 25 ? "border-success/60" : "border-primary/20"
          }`}
          title={
            ufficiali >= 25
              ? tr("Lista convocati piena")
              : tr("{{v1}} slot convocati liberi", { v1: 25 - ufficiali })
          }
        >
          <div className="font-display text-2xl text-success leading-none">
            {ufficiali}
            <span className="text-sm text-fg-muted">/25</span>
          </div>
          <div className="text-[10px] text-fg-muted uppercase tracking-wider mt-1">
            {tr("Convocati")}
          </div>
        </div>
        <div className="bg-bg-card border border-primary/20 rounded-card p-3 text-center">
          <div className="font-display text-2xl text-warning leading-none">{provvisorie}</div>
          <div className="text-[10px] text-fg-muted uppercase tracking-wider mt-1">
            {tr("Non convocati")}
          </div>
        </div>
      </div>

      {/* Andrea 2026-09-24: quanto e' costata la rosa e quanto vale su TM */}
      {rosa && rosa.items.length > 0 && (
        <div className="bg-bg-card border border-primary/20 rounded-card px-3 py-2 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[11px]">
          <span className="text-fg-muted">
            {tr("Pagato")} <b className="font-mono text-fg">{valoriRosa.pagato.toFixed(0)}M</b>
          </span>
          <span className="text-fg-muted">
            {tr("TM all'acquisto")}{" "}
            <b className="font-mono text-fg">{valoriRosa.tmIniziale.toFixed(1)}M</b>
          </span>
          <span className="text-fg-muted">
            {tr("TM oggi")} <b className="font-mono text-accent">{valoriRosa.tmOggi.toFixed(1)}M</b>
            {Math.abs(valoriRosa.variazione) >= 0.05 && (
              <b
                className={`font-mono ml-1 ${
                  valoriRosa.variazione > 0 ? "text-success" : "text-danger"
                }`}
              >
                {valoriRosa.variazione > 0 ? "▲" : "▼"}
                {Math.abs(valoriRosa.variazione).toFixed(1)}M
              </b>
            )}
          </span>
        </div>
      )}

      {/* Filtri chip */}
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
        {(["tutti", "P", "D", "C", "A"] as const).map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => setFilter(c)}
            className={`px-3 py-1.5 rounded-pill text-xs font-bold whitespace-nowrap transition ${
              filter === c
                ? "bg-accent text-ink"
                : "bg-bg-card text-fg-muted border border-primary/20"
            }`}
          >
            {c === "tutti" ? tr("Tutti") : RUOLO_TAG[c].label}
          </button>
        ))}
      </div>

      {!stagioneAttiva && (
        <div className="card text-fg-muted text-center p-4">{tr("Nessuna stagione attiva.")}</div>
      )}

      {/* Player list */}
      {isLoading && stagioneId && squadraId && (
        <div className="bg-bg-card border border-primary/20 rounded-card p-4 text-center text-fg-muted">
          {tr("Caricamento…")}
        </div>
      )}

      {(!stagioneId || !squadraId) && (
        <div className="bg-bg-card border border-primary/20 rounded-card p-6 text-center">
          <div className="text-3xl mb-2">📋</div>
          <div className="text-fg-muted text-sm">
            {tr("Inserisci una stagione per visualizzare i giocatori.")}
          </div>
          <div className="text-[10px] text-fg-muted mt-2">
            {tr("La rosa apparirà qui una volta importato il listone e creata la stagione.")}
          </div>
        </div>
      )}

      {rosa && filteredItems.length === 0 && stagioneId && (
        <div className="bg-bg-card border border-primary/20 rounded-card p-4 text-center text-fg-muted text-sm">
          {tr("Nessun giocatore con questo filtro.")}
        </div>
      )}

      {/* Sezione 1: Convocati (lista 25, ufficiali) */}
      {convocati.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2 px-1 pt-2">
            <h3 className="font-display text-lg text-success">{tr("⭐ Convocati")}</h3>
            {/* Andrea 2026-09-24: qui in alto a destra i download Excel dei
                valori, accanto al conteggio. */}
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] font-mono text-fg-muted uppercase tracking-wider">
                {convocati.length} {convocati.length === 1 ? "giocatore" : "giocatori"}
              </span>
              <button
                type="button"
                onClick={() => scaricaValori("squadra")}
                disabled={scaricoValori !== null || !stagioneId}
                className="px-1.5 py-0.5 text-[10px] whitespace-nowrap rounded border border-success/40 text-success hover:bg-success/10 disabled:opacity-50"
                title={tr(
                  tr(
                    "Excel con prezzo pagato, valore TM all'acquisto, valore TM di oggi e variazione di ogni giocatore",
                  ),
                )}
              >
                {scaricoValori === "squadra" ? "…" : tr("⬇ Excel")}
              </button>
              {/* Andrea 2026-09-24: la lega intera la scaricano tutti. */}
              {stagioneId && (
                <button
                  type="button"
                  onClick={() => scaricaValori("lega")}
                  disabled={scaricoValori !== null || !stagioneId}
                  className="px-1.5 py-0.5 text-[10px] whitespace-nowrap rounded border border-success/40 text-success hover:bg-success/10 disabled:opacity-50"
                  title={tr(
                    tr("Stesso Excel per tutte le squadre della lega, con un foglio di riepilogo"),
                  )}
                >
                  {scaricoValori === "lega" ? "…" : tr("⬇ Lega")}
                </button>
              )}
            </div>
          </div>
          {convocati.map((p) => (
            <PlayerCard
              key={p.iscrizione_id}
              p={p}
              // Andrea 2026-05-21: convoca/escludi inline solo a lista aperta.
              // Se chiusa, il backend rifiuta comunque ma per UX puliamo
              // l'UI (no bottoni dove non si possono cliccare).
              onConvoca={listaChiusa ? undefined : onConvoca}
              onEscludi={listaChiusa ? undefined : onEscludi}
              busy={rosterBusy}
            />
          ))}
        </div>
      )}

      {/* Sezione 2: Fuori lista 25 (rosa estesa / provvisoria) */}
      {fuoriLista.length > 0 && (
        <div className="space-y-2 pt-2">
          <div className="flex items-baseline justify-between gap-2 px-1 pt-2 border-t border-primary/20">
            <h3 className="font-display text-lg text-fg-muted">{tr("📋 Non convocati")}</h3>
            <span className="text-[10px] font-mono text-fg-muted uppercase tracking-wider">
              {fuoriLista.length} {fuoriLista.length === 1 ? "giocatore" : "giocatori"}
            </span>
          </div>
          {fuoriLista.map((p) => (
            <PlayerCard
              key={p.iscrizione_id}
              p={p}
              onConvoca={listaChiusa ? undefined : onConvoca}
              onEscludi={listaChiusa ? undefined : onEscludi}
              busy={rosterBusy}
            />
          ))}
        </div>
      )}

      {swapEsito && (
        <div
          role="status"
          className="fixed bottom-20 left-1/2 -translate-x-1/2 z-50 rounded-card border border-success/40 bg-bg-card px-4 py-2 text-sm text-success shadow-hero flex items-center gap-3"
        >
          <span>{swapEsito}</span>
          <button
            type="button"
            onClick={() => setSwapEsito(null)}
            className="text-fg-muted hover:text-fg"
          >
            ✕
          </button>
        </div>
      )}
      {/* Modal swap rosa (mercato chiuso, 1 per finestra) */}
      {showSwapModal && rosa && stagioneId && squadraId && (
        <SwapRosaModal
          convocati={convocati}
          fuoriLista={fuoriLista}
          isPending={swapMut.isPending}
          errore={swapErrore}
          onCancel={() => {
            setShowSwapModal(false);
            setSwapErrore(null);
          }}
          onConfirm={async (uscenteId, entranteId) => {
            setSwapErrore(null);
            try {
              await swapMut.mutateAsync({
                stagioneId,
                squadraId,
                uscenteId,
                entranteId,
              });
              setShowSwapModal(false);
              setSwapEsito(tr("✓ Swap completato"));
            } catch (e) {
              const err = e as {
                response?: { data?: { detail?: string }; status?: number };
                message?: string;
              };
              const status = err.response?.status;
              const detail = err.response?.data?.detail ?? err.message ?? tr("errore");
              setSwapErrore(
                status === 409 && detail.toLowerCase().includes("aperto")
                  ? tr(
                      "Lista 25 non ancora ufficializzata: usa Convoca/Escludi normali. Lo swap si attiva solo dopo che l'admin ha ufficializzato la lista 25.",
                    )
                  : detail,
              );
            }
          }}
        />
      )}

      {/* Andrea 2026-05-21: Modal allenatori in seconda */}
      {showCediModal && squadraId && squadraCorrente && (
        <CediSquadraModal
          squadraId={squadraId}
          nomeSquadra={squadraCorrente.nome}
          onClose={() => setShowCediModal(false)}
        />
      )}
      {showSecondiModal && squadraId && (
        <AllenatoriSecondiModal squadraId={squadraId} onClose={() => setShowSecondiModal(false)} />
      )}

      {/* Andrea 2026-06-11: Modal editor maglia */}
      {logoDaScegliere && (
        <LogoSfondoModal
          originale={logoDaScegliere.originale}
          senzaSfondo={logoDaScegliere.senzaSfondo}
          inCorso={updateLogoMut.isPending}
          onScegli={(dataUrl) => salvaLogo(logoDaScegliere.squadraId, dataUrl)}
          onClose={() => setLogoDaScegliere(null)}
        />
      )}

      {magliaEditorOpen && squadraCorrente && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-0 sm:p-4"
          onClick={() => setMagliaEditorOpen(false)}
        >
          <div
            className="bg-bg-card border border-accent/30 sm:rounded-2xl rounded-t-2xl shadow-[0_20px_60px_rgba(0,0,0,0.6)] w-full max-w-4xl max-h-[94vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Andrea 2026-10-07: finestra della maglia piu' curata */}
            <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-primary/30 bg-gradient-to-r from-primary-deep to-bg-card">
              <div className="min-w-0">
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent">
                  {tr("Laboratorio maglie")}
                </div>
                <h3 className="font-display text-xl text-fg truncate">{squadraCorrente.nome}</h3>
              </div>
              <button
                type="button"
                onClick={() => setMagliaEditorOpen(false)}
                className="shrink-0 w-9 h-9 rounded-full border border-primary/40 text-fg-muted hover:text-fg hover:border-accent/50"
                aria-label={tr("Chiudi")}
              >
                ✕
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-4">
              <MagliaEditor
                value={magliaDraft}
                onChange={setMagliaDraft}
                logoDataUrl={squadraCorrente?.logo_data_url}
                nomeSquadra={squadraCorrente.nome}
                ampio
              />
            </div>
            <div className="flex gap-2 justify-end px-5 py-3 border-t border-primary/30 bg-primary-deep/40">
              <button
                type="button"
                onClick={() => setMagliaEditorOpen(false)}
                className="btn-ghost text-sm"
              >
                {tr("Annulla")}
              </button>
              <button
                type="button"
                onClick={onSalvaMaglia}
                disabled={updateMagliaMut.isPending}
                className="btn-primary text-sm disabled:opacity-50 min-w-[9rem]"
              >
                {updateMagliaMut.isPending ? tr("Salvataggio…") : tr("👕 Salva maglia")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal designazione capitano + vicecapitano stagionale */}
      {showCapModal && rosa && stagioneId && squadraId && (
        <CapitanoModal
          convocati={convocati}
          poolIniziale={capStagione?.pool_giocatori_ids ?? []}
          isPending={setCapMut.isPending}
          onCancel={() => setShowCapModal(false)}
          onSave={async (poolIds) => {
            try {
              await setCapMut.mutateAsync({
                stagioneId,
                squadraId,
                payload: { pool_giocatori_ids: poolIds },
              });
              setShowCapModal(false);
            } catch (e) {
              const err = e as {
                response?: { data?: { detail?: string } };
                message?: string;
              };
              alert(tr("Errore: ") + (err.response?.data?.detail ?? err.message ?? "errore"));
            }
          }}
        />
      )}
    </MobileShell>
  );
}

// ============================================================
// Modal pool papabili capitani (esattamente 5)
// ============================================================
interface CapitanoModalProps {
  convocati: RosaPlayer[];
  poolIniziale: string[];
  isPending: boolean;
  onCancel: () => void;
  onSave: (poolIds: string[]) => void;
}

const POOL_SIZE = 5;

function CapitanoModal({
  convocati,
  poolIniziale,
  isPending,
  onCancel,
  onSave,
}: CapitanoModalProps) {
  const [selezione, setSelezione] = useState<string[]>(poolIniziale);
  const toggle = (gid: string) => {
    setSelezione((cur) => {
      if (cur.includes(gid)) return cur.filter((x) => x !== gid);
      if (cur.length >= POOL_SIZE) return cur; // max 5
      return [...cur, gid];
    });
  };
  const valid = selezione.length === POOL_SIZE;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <div
        className="bg-bg-card border border-accent/40 rounded-card max-w-md w-full p-4 space-y-3 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="font-display text-xl text-accent">
          Papabili capitani ({selezione.length}/{POOL_SIZE})
        </div>
        <p className="text-xs text-fg-muted">
          {tr("Scegli")} <b>esattamente {POOL_SIZE}</b>{" "}
          {tr("giocatori dalla lista 25 ufficiale. Ad ogni giornata, in")}{" "}
          <b>{tr("Schiera Formazione")}</b>
          {tr(", sceglierai capitano e vice tra questi 5. Il pool si")} <b>{tr("congela")}</b>{" "}
          {tr("alla chiusura del mercato.")}
        </p>

        <div className="space-y-1">
          {convocati.map((p) => {
            const checked = selezione.includes(p.giocatore_id);
            const disabled = !checked && selezione.length >= POOL_SIZE;
            return (
              <label
                key={p.giocatore_id}
                className={`flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer text-sm ${
                  checked
                    ? "bg-accent/15 border border-accent/40"
                    : "border border-transparent hover:bg-primary-deep/40"
                } ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={disabled}
                  onChange={() => toggle(p.giocatore_id)}
                />
                <span className="flex-1 truncate">{p.nome}</span>
                <span className="text-[10px] text-fg-muted">
                  {(p.ruoli_mantra ? sigleRuoli(p.ruoli_mantra) : p.ruolo_classic) ?? "?"}
                </span>
              </label>
            );
          })}
        </div>

        {!valid && (
          <div className="text-xs text-warning">
            Seleziona esattamente {POOL_SIZE} giocatori (ora {selezione.length}).
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-primary-soft sticky bottom-0 bg-bg-card">
          <button type="button" onClick={onCancel} className="btn-ghost text-sm">
            {tr("Annulla")}
          </button>
          <button
            type="button"
            disabled={!valid || isPending}
            onClick={() => onSave(selezione)}
            className="btn-primary text-sm disabled:opacity-50"
          >
            {isPending ? tr("Salvataggio…") : tr("💾 Salva i 5")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Modal swap rosa (mercato chiuso, 1 per finestra)
// ============================================================
interface SwapRosaModalProps {
  convocati: RosaPlayer[];
  fuoriLista: RosaPlayer[];
  isPending: boolean;
  /** Andrea 2026-10-16: l'errore si mostra nel modal, non con l'alert del browser */
  errore?: string | null;
  onCancel: () => void;
  onConfirm: (uscenteId: string, entranteId: string) => void;
}

function SwapRosaModal({
  convocati,
  fuoriLista,
  isPending,
  errore,
  onCancel,
  onConfirm,
}: SwapRosaModalProps) {
  const [uscenteId, setUscenteId] = useState("");
  const [entranteId, setEntranteId] = useState("");
  const valid = !!uscenteId && !!entranteId && uscenteId !== entranteId;
  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <div
        className="bg-bg-card border border-accent/40 rounded-card max-w-md w-full p-4 space-y-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="font-display text-xl text-accent">{tr("↔ Swap rosa")}</div>
        <p className="text-xs text-fg-muted">
          Sostituisci 1 giocatore nella lista 25 ufficiale.{" "}
          <b>{tr("1 swap per finestra mercato chiusa")}</b>
          {tr(": se non lo usi, lo perdi all'apertura della finestra successiva.")}
        </p>

        <div>
          <label className="block text-[10px] uppercase tracking-wider text-fg-muted mb-1">
            {tr("Esce dalla lista 25 (giocatore ufficiale)")}
          </label>
          <select
            value={uscenteId}
            onChange={(e) => setUscenteId(e.target.value)}
            className="w-full px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg text-sm"
          >
            <option value="">{tr("— scegli giocatore in uscita —")}</option>
            {convocati.map((p) => (
              <option key={p.giocatore_id} value={p.giocatore_id}>
                {p.nome} ({(p.ruoli_mantra ? sigleRuoli(p.ruoli_mantra) : p.ruolo_classic) ?? "?"})
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-[10px] uppercase tracking-wider text-fg-muted mb-1">
            {tr("Entra in lista 25 (giocatore fuori lista, posseduto)")}
          </label>
          <select
            value={entranteId}
            onChange={(e) => setEntranteId(e.target.value)}
            className="w-full px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg text-sm"
          >
            <option value="">{tr("— scegli giocatore in entrata —")}</option>
            {fuoriLista.map((p) => (
              <option key={p.giocatore_id} value={p.giocatore_id}>
                {p.nome} ({(p.ruoli_mantra ? sigleRuoli(p.ruoli_mantra) : p.ruolo_classic) ?? "?"})
              </option>
            ))}
          </select>
        </div>

        {errore && (
          <div className="rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
            ⚠ {errore}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-primary-soft">
          <button type="button" onClick={onCancel} className="btn-ghost text-sm">
            {tr("Annulla")}
          </button>
          <button
            type="button"
            disabled={!valid || isPending}
            onClick={() => onConfirm(uscenteId, entranteId)}
            className="btn-primary text-sm disabled:opacity-50"
          >
            {isPending ? tr("Swap in corso…") : tr("↔ Conferma swap")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// AllenatoriSecondiModal — invita/lista/rimuovi soci (Andrea 2026-05-21)
// ============================================================
interface AllenatoriSecondiModalProps {
  squadraId: string;
  onClose: () => void;
}

function AllenatoriSecondiModal({ squadraId, onClose }: AllenatoriSecondiModalProps) {
  const { data: secondi, isLoading } = useAllenatoriSecondi(squadraId);
  const invitaMut = useInvitaAllenatoreSecondo();
  const rimuoviMut = useRimuoviAllenatoreSecondo();
  const [email, setEmail] = useState("");
  const [lastInvito, setLastInvito] = useState<{
    codice: string;
    link: string;
    email: string;
  } | null>(null);

  async function onInvita(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    try {
      const r = await invitaMut.mutateAsync({
        squadraId,
        emailAllenatore: email.trim(),
      });
      setLastInvito({
        codice: r.codice_leggibile,
        link: r.link_invito,
        email: r.email_destinatario,
      });
      setEmail("");
    } catch (e) {
      const err = e as {
        response?: { data?: { detail?: string } };
        message?: string;
      };
      alert(tr("Errore: {{v1}}", { v1: err.response?.data?.detail ?? err.message ?? "errore" }));
    }
  }

  async function onRimuovi(userId: string, nome: string) {
    if (!confirm(tr("Rimuovere {{v1}} come allenatore in seconda?", { v1: nome }))) return;
    try {
      await rimuoviMut.mutateAsync({ squadraId, userId });
    } catch (e) {
      const err = e as {
        response?: { data?: { detail?: string } };
        message?: string;
      };
      alert(tr("Errore: {{v1}}", { v1: err.response?.data?.detail ?? err.message ?? "errore" }));
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-2 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-bg-card border border-accent/40 rounded-card w-full max-w-md max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-primary-soft flex items-center justify-between">
          <div className="font-display text-lg text-accent">{tr("👥 Allenatori in seconda")}</div>
          <button
            onClick={onClose}
            className="text-fg-muted hover:text-fg text-2xl leading-none"
            aria-label={tr("Chiudi")}
          >
            ×
          </button>
        </div>
        <div className="overflow-y-auto flex-1 p-4 space-y-4">
          <p className="text-xs text-fg-muted">
            {tr(
              tr(
                "Invita un socio a co-gestire la tua squadra (formazione, rosa, mercato). Non ottiene i tuoi privilegi di presidente (rinomina, logo, privacy).",
              ),
            )}
          </p>

          {/* Form invito */}
          <form onSubmit={onInvita} className="space-y-2">
            <label className="text-xs text-fg-muted uppercase tracking-wider">
              {tr("Email del socio da invitare")}
            </label>
            <div className="flex gap-2">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="socio@email.com"
                className="flex-1 px-3 py-2 bg-primary-deep border border-primary-soft rounded text-fg text-sm"
              />
              <button
                type="submit"
                disabled={invitaMut.isPending}
                className="btn-primary text-sm disabled:opacity-50"
              >
                {invitaMut.isPending ? "…" : tr("Invita")}
              </button>
            </div>
          </form>

          {/* Esito ultimo invito */}
          {lastInvito && (
            <div className="bg-success/10 border border-success/30 rounded-card p-3 text-xs space-y-1">
              <div className="font-bold text-success">✓ Invito inviato a {lastInvito.email}</div>
              <div className="font-mono text-fg-muted">
                {tr("Codice:")} <span className="text-accent font-bold">{lastInvito.codice}</span>
              </div>
              <div className="text-fg-muted break-all">Link: {lastInvito.link}</div>
            </div>
          )}

          {/* Lista attuali */}
          <div className="space-y-2">
            <div className="text-xs font-bold text-fg-muted uppercase tracking-wider">
              Allenatori attuali ({secondi?.length ?? 0})
            </div>
            {isLoading && <div className="text-xs text-fg-muted italic">{tr("Caricamento…")}</div>}
            {!isLoading && (!secondi || secondi.length === 0) && (
              <div className="text-xs text-fg-muted italic">
                {tr("Nessun allenatore in seconda. Invita un socio sopra.")}
              </div>
            )}
            {secondi?.map((a) => (
              <div
                key={a.id}
                className="bg-primary-deep/40 border border-primary-soft rounded-card p-2 flex items-center justify-between gap-2"
              >
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-sm text-fg truncate">{a.user_nome_completo}</div>
                  <div className="text-[10px] text-fg-muted truncate">{a.user_email}</div>
                </div>
                <button
                  type="button"
                  onClick={() => onRimuovi(a.user_id, a.user_nome_completo)}
                  disabled={rimuoviMut.isPending}
                  className="text-[10px] uppercase tracking-wider text-danger hover:underline shrink-0 disabled:opacity-50"
                  title={tr("Rimuovi questo socio dalla squadra")}
                >
                  {tr("✕ Rimuovi")}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
